// Tab 5 · Env variables (owner, 2026-10-17): everything the batch code reads as ctx.env.NAME, with a UAT and a
// production value. A name the code uses that the lender does not have yet appears here by itself (from discovery)
// as "from code · missing". Values are written to .env in the playground's container and to the vault in
// production; never to git, storage or a log. The demo data holds placeholders only.
import { useState } from 'react'
import { KeyRound, Plus, Trash2 } from 'lucide-react'
import { Badge, Can, ConfirmDialog, PromptDialog, formatRelative } from '@/kit/ui'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { useHubStore } from '@/store/hubStore'
import type { EnvVar, Lender } from '@/types'

const SOURCE: Record<EnvVar['source'], { label: string; tone: 'primary' | 'warning' | 'gray' }> = { hub: { label: 'set by the Hub', tone: 'primary' }, code: { label: 'from code', tone: 'warning' }, manual: { label: 'added by hand', tone: 'gray' } }

/** LENDER_BASE_URL is never typed here: it follows Lender info, also when its row was added by hand */
const valueOf = (lender: Lender, v: EnvVar, env: 'uat' | 'prod') =>
  v.name === 'LENDER_BASE_URL' ? (env === 'uat' ? lender.env.uatBaseUrl : lender.env.prodBaseUrl) || v[env] : v[env]

function Value({ lender, v, env }: { lender: Lender; v: EnvVar; env: 'uat' | 'prod' }) {
  const value = valueOf(lender, v, env)
  if (v.name === 'LENDER_BASE_URL') return value ? <span><code className="text-2xs">{value}</code> <Badge tone="gray">from Lender info</Badge></span> : <Badge tone="danger">missing in Lender info</Badge>
  if (value === null || value === '') return <Badge tone="danger">missing</Badge>
  if (v.secret) return <span className="font-mono text-2xs">{value.startsWith('•') ? value : `${value.slice(0, 3)}••••••`} <Badge tone="success">set</Badge></span>
  return <code className="text-2xs">{value}</code>
}

export function EnvTab({ lender }: { lender: Lender }) {
  const { user, can } = useCurrentUser()
  const setEnvVar = useHubStore((s) => s.setEnvVar)
  const removeEnvVar = useHubStore((s) => s.removeEnvVar)
  const [editing, setEditing] = useState<{ name: string; env: 'uat' | 'prod' } | null>(null)
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<string | null>(null)
  const missing = lender.envVars.filter((v) => !valueOf(lender, v, 'uat'))
  const canEdit = can('lenders.edit')
  const canProd = can('gateway.manage')

  return (
    <>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <p className="max-w-3xl text-xs text-muted">What the batch code reads as <code>ctx.env.NAME</code>. The playground writes these into the container's <code>.env</code> (UAT); the live container gets the production values from the vault. A name the code uses that is not here yet appears by itself after Discover or Save, marked <b>from code · missing</b>.</p>
        <Can permission="lenders.edit"><button type="button" className="btn btn-primary" onClick={() => setAdding(true)} data-testid="env-add"><Plus size={13} /> Add variable</button></Can>
      </div>
      {missing.length > 0 && (
        <div className="mb-3 rounded border-l-[3px] border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200" data-testid="env-missing">
          <b>{missing.length} variable{missing.length === 1 ? '' : 's'} without a UAT value:</b> {missing.map((v) => v.name).join(', ')}. Runs that read them fail with “no variable”.
        </div>
      )}
      <div className="card-flush overflow-x-auto">
        <table className="table">
          <thead><tr><th>Name</th><th>UAT value</th><th>Production value</th><th>Source</th><th>Used in</th><th>Updated</th><th /></tr></thead>
          <tbody>
            {lender.envVars.map((v) => (
              <tr key={v.name} data-testid={`env-${v.name}`}>
                <td className="mono"><b>{v.name}</b>{v.secret && <KeyRound size={11} className="ml-1 inline text-muted" aria-label="secret, masked" />}</td>
                <td>
                  <Value lender={lender} v={v} env="uat" />
                  {canEdit && v.name !== 'LENDER_BASE_URL' && <button type="button" className="btn btn-ghost btn-sm ml-1" onClick={() => setEditing({ name: v.name, env: 'uat' })} aria-label={`Set UAT value of ${v.name}`}>set</button>}
                </td>
                <td>
                  <Value lender={lender} v={v} env="prod" />
                  {canProd && v.name !== 'LENDER_BASE_URL' && <button type="button" className="btn btn-ghost btn-sm ml-1" onClick={() => setEditing({ name: v.name, env: 'prod' })} aria-label={`Set production value of ${v.name}`}>set</button>}
                </td>
                <td><Badge tone={SOURCE[v.source].tone}>{SOURCE[v.source].label}</Badge></td>
                <td className="mono text-2xs">{v.usedIn.join(', ') || '—'}</td>
                <td className="text-2xs">{v.updatedBy.split('@')[0]} · {formatRelative(v.updatedAt)}</td>
                <td className="text-right">{canEdit && v.source !== 'hub' && <button type="button" className="icon-btn" aria-label={`Remove ${v.name}`} onClick={() => setRemoving(v.name)}><Trash2 size={13} /></button>}</td>
              </tr>
            ))}
            {!lender.envVars.length && <tr><td colSpan={7} className="text-xs text-muted">No variable yet. LENDER_BASE_URL comes from Lender info when the first batch is discovered.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-2xs text-muted"><b>LENDER_BASE_URL</b> follows Lender info (UAT / production addresses). <b>LENDER_TOKEN</b> is the lender's fixed key or token: set its UAT and production values here like any other. Secrets are shown once when set, then masked. Admins set production values; editors set UAT.</p>

      <PromptDialog
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        title={`${editing?.name} · ${editing?.env === 'uat' ? 'UAT' : 'production'} value`}
        message={editing?.env === 'prod' ? 'Goes to the vault; the live container reads it at the next Make live. Shown once.' : 'Goes to the playground container’s .env the next time it opens (or Refresh .env). Shown once.'}
        inputLabel="Value"
        initialValue=""
        placeholder="paste the value"
        validate={(v) => (v.trim() ? null : 'Type a value')}
        confirmText="Set"
        onConfirm={(v) => { if (editing) setEnvVar(lender.slug, editing.name, { [editing.env]: v.trim() }, user.email); setEditing(null) }}
      />
      <PromptDialog
        isOpen={adding}
        onClose={() => setAdding(false)}
        title="Add a variable"
        message="UPPER_CASE with underscores, e.g. PARTNER_ID. Then set its UAT and production values."
        inputLabel="Name"
        initialValue=""
        placeholder="PARTNER_ID"
        validate={(v) => (!/^[A-Z_][A-Z0-9_]*$/.test(v) ? 'UPPER_CASE letters, numbers and _' : lender.envVars.some((x) => x.name === v) ? `${v} is already here` : null)}
        confirmText="Add"
        onConfirm={(v) => { setEnvVar(lender.slug, v, {}, user.email); setAdding(false) }}
      />
      <ConfirmDialog isOpen={removing !== null} onClose={() => setRemoving(null)} type="danger" title={`Remove ${removing}?`} message="Runs that read it will fail with “no variable”. If the code still uses it, it comes back as “from code · missing” at the next Discover." confirmText="Remove" onConfirm={() => { if (removing) removeEnvVar(lender.slug, removing, user.email); setRemoving(null) }} />
    </>
  )
}
